import {
  decodeServerMessage,
  encodeChat,
  encodeInput,
  encodeJoin,
  encodeLeaveMatch,
  encodePadReady,
  encodeVoteDifficulty,
  encodePing,
  encodeRematch,
  encodeSelectLoadout,
  encodeVoiceSignal,
  encodeVoteMap,
  type AnyServerMessage,
} from '@game/protocol';
import type { InputCommand, PlayerId, WeaponId } from '@game/shared';

export type ConnectionStatus = 'idle' | 'connecting' | 'connected' | 'closed' | 'error';

export interface ConnectionEvents {
  onStatus(status: ConnectionStatus, detail?: string): void;
  onMessage(message: AnyServerMessage): void;
}

/**
 * By default the socket goes back to whatever host served the page, which is
 * what makes LAN play work with no configuration: a laptop opening
 * `http://<host-ip>:5173` gets proxied straight through to the game server.
 *
 * `VITE_GAME_WS_URL` overrides it for clients that are not served by the game
 * host at all — a deployed frontend pointing at a remote match server, or the
 * desktop shell, which loads its bundle from disk.
 */
const defaultUrl = (): string => {
  const override = import.meta.env.VITE_GAME_WS_URL;
  if (override) return override;
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/ws`;
};

/**
 * WebSocket transport. Deliberately behind a thin interface: WebTransport (or
 * WebRTC data channels) over UDP is the eventual home for input and snapshots,
 * and only this file should need to change.
 */
export class GameConnection {
  private socket: WebSocket | null = null;
  private pingTimer: number | null = null;

  /** Smoothed round-trip time in milliseconds. */
  rttMs = 0;
  status: ConnectionStatus = 'idle';

  constructor(
    private readonly events: ConnectionEvents,
    private readonly url: string = defaultUrl(),
  ) {}

  /**
   * @param matchId which match to join; omitted joins whichever the server lists first.
   * @param session REST session token, so the server knows which account this is.
   */
  connect(name: string, character: number, matchId?: string, session = ''): void {
    this.disconnect();
    this.setStatus('connecting');

    const target = matchId
      ? `${this.url}${this.url.includes('?') ? '&' : '?'}match=${encodeURIComponent(matchId)}`
      : this.url;
    const socket = new WebSocket(target);
    socket.binaryType = 'arraybuffer';
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.setStatus('connected');
      this.send(encodeJoin(name, character, session));
      this.pingTimer = window.setInterval(() => this.ping(), 1000);
      this.ping();
    });

    socket.addEventListener('message', (event) => {
      if (!(event.data instanceof ArrayBuffer)) return;
      const message = decodeServerMessage(new Uint8Array(event.data));
      if (!message) return;
      this.events.onMessage(message);
    });

    socket.addEventListener('close', (event) => {
      this.clearPing();
      this.setStatus('closed', event.reason || undefined);
    });

    socket.addEventListener('error', () => {
      this.setStatus('error', 'connection failed');
    });
  }

  disconnect(): void {
    this.clearPing();
    if (this.socket && this.socket.readyState <= WebSocket.OPEN) this.socket.close();
    this.socket = null;
  }

  get isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  sendInput(tick: number, commands: readonly InputCommand[], ackTick: number): void {
    this.send(encodeInput(tick, commands, ackTick));
  }

  sendChat(text: string): void {
    this.send(encodeChat(text));
  }

  sendLoadout(weapon: WeaponId): void {
    this.send(encodeSelectLoadout(weapon));
  }

  sendVote(mapIndex: number): void {
    this.send(encodeVoteMap(mapIndex));
  }

  sendVoteDifficulty(tier: number): void {
    this.send(encodeVoteDifficulty(tier));
  }

  sendPadReady(): void {
    this.send(encodePadReady());
  }

  sendRematch(): void {
    this.send(encodeRematch());
  }

  sendLeaveMatch(): void {
    this.send(encodeLeaveMatch());
  }

  sendVoiceSignal(to: PlayerId, data: string): void {
    this.send(encodeVoiceSignal(to, data));
  }

  /** Call from the message handler when a Pong arrives. */
  recordPong(clientTimeMs: number): void {
    const sample = performance.now() - clientTimeMs;
    this.rttMs = this.rttMs === 0 ? sample : this.rttMs * 0.85 + sample * 0.15;
  }

  private ping(): void {
    this.send(encodePing(performance.now()));
  }

  private send(bytes: Uint8Array): void {
    if (!this.isOpen) return;
    // A slice is required: `bytes` may be a view into a reused buffer.
    this.socket!.send(bytes.slice().buffer);
  }

  private clearPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setStatus(status: ConnectionStatus, detail?: string): void {
    this.status = status;
    this.events.onStatus(status, detail);
  }
}
